<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background mit modernen Farben für Kassensystem -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background - professionelle Farben -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50/30 via-white to-purple-50/40 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"></div>
      
      <!-- Animierte Blobs in modernen Farben -->
      <!-- Blauer Blob (Hauptfarbe) -->
      <div class="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-500/8 dark:bg-blue-500/5 rounded-[60%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-60 animate-blob"></div>
      
      <!-- Indigo Blob -->
      <div class="absolute top-1/4 right-0 w-[600px] h-[600px] bg-indigo-400/10 dark:bg-indigo-500/6 rounded-[50%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
      
      <!-- Lila Blob -->
      <div class="absolute bottom-0 left-1/4 w-[550px] h-[550px] bg-purple-300/12 dark:bg-purple-400/6 rounded-[55%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-45 animate-blob animation-delay-4000"></div>
      
      <!-- Zusätzlicher sanfter Blob für Tiefe -->
      <div class="absolute top-1/2 right-1/3 w-[400px] h-[400px] bg-blue-300/8 dark:bg-blue-400/4 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-35 animate-blob-organic animation-delay-1500"></div>
      
      <!-- Grid Pattern Overlay für moderne Struktur -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] opacity-60"></div>
      
      <!-- Subtile Wellen-Effekte -->
      <div class="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-blue-100/20 via-transparent to-transparent dark:from-blue-900/10"></div>
    </div>

    <Navigation />

    <!-- Hero Section -->
    <section class="pt-36 pb-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row items-center gap-12 mb-12">
          <!-- Text Content -->
          <div class="flex-1 text-center lg:text-left">
            <div v-if="logoUrl" class="flex justify-center lg:justify-start mb-8 reveal">
              <img 
                :key="`verkaufszaehler-logo-${isDark}-${logoUrl}`"
                :src="logoUrl"
                alt="Verkaufszähler Logo" 
                class="h-20 md:h-24 object-contain transition-opacity duration-300"
              />
            </div>
            <div class="inline-block mb-6 reveal">
              <div class="px-5 py-2.5 bg-white dark:bg-gray-800/80 backdrop-blur-md rounded-full border-2 border-blue-400/30 dark:border-blue-500/30 shadow-md">
                <span class="text-sm font-medium text-gray-800 dark:text-white">💳 Kassensystem</span>
              </div>
            </div>
            <h1 class="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 reveal delay-100 leading-tight">
              Verkaufszähler
            </h1>
            <div class="mt-4 h-2 w-32 bg-blue-500 mx-auto lg:mx-0 rounded-full reveal delay-200"></div>
            <p class="mt-6 text-xl md:text-2xl font-medium text-gray-700 dark:text-white reveal delay-300">
              Modernes Kassensystem für den mobilen Einsatz
            </p>
            <div class="mt-8 text-lg text-gray-600 dark:text-gray-400 leading-relaxed reveal delay-400 space-y-4">
              <p>
                Der Verkaufszähler ist eine <span class="font-bold">moderne Webanwendung</span>, die als Kassensystem für den mobilen Einsatz auf Tablets konzipiert wurde. Die Software wurde entwickelt, um traditionelle Strichlisten zu ersetzen und den Verkaufsprozess vor Ort zu beschleunigen.
              </p>
              <p>
                Mit einem <span class="font-bold">intuitiven Dashboard</span>, umfassenden Admin-Funktionen und detaillierten Statistiken bietet die Anwendung alles, was für einen effizienten Verkaufsbetrieb benötigt wird.
              </p>
              <p class="text-base font-semibold">
                Entwickelt mit <span class="text-blue-500 dark:text-blue-400">React</span>, <span class="text-blue-500 dark:text-blue-400">TypeScript</span> und <span class="text-blue-500 dark:text-blue-400">Firebase</span> für maximale Performance und Offline-Funktionalität.
              </p>
            </div>
          </div>

          <!-- Hero Screenshot -->
          <div class="flex-1 w-full max-w-2xl reveal delay-300">
            <div class="relative group">
              <div class="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div class="relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer" @click="openLightbox('/projects/verkaufszaehler/hero.webp')">
                  <img 
                    src="/projects/verkaufszaehler/hero.webp"
                  alt="Verkaufszähler auf iPad" 
                  class="w-full h-auto object-cover hover:scale-105 transition duration-700"
                />
                <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div class="bg-white/20 backdrop-blur-md p-3 rounded-full">
                    <MagnifyingGlassPlusIcon class="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tech Stack -->
        <div class="mb-12 reveal delay-500">
          <div class="flex flex-wrap justify-center gap-2 md:gap-3">
            <span
              v-for="tech in displayedTechnologies"
              :key="tech"
              class="px-2 py-1 md:px-4 md:py-2 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-full md:rounded-full text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-md md:shadow-lg border-2 border-blue-200/50 dark:border-gray-600/50 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xl transition-all duration-300"
            >
              {{ tech }}
            </span>
          </div>
          <!-- "Mehr anzeigen" Button nur auf mobil -->
          <button
            v-if="!showAllTechs && project.technologies.length > 8"
            @click="showAllTechs = true"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-blue-500 dark:text-blue-400 hover:underline transition-colors"
          >
            + {{ project.technologies.length - 8 }} weitere anzeigen
          </button>
          <button
            v-else-if="showAllTechs"
            @click="showAllTechs = false"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-blue-500 dark:text-blue-400 hover:underline transition-colors"
          >
            Weniger anzeigen
          </button>
        </div>
      </div>
    </section>

    <!-- Project Stats / Highlights Grid -->
    <section class="pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 relative">
      <div class="max-w-7xl mx-auto mb-12 md:mb-16">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div v-for="(stat, i) in stats" :key="i" class="bg-white dark:bg-gray-800/80 backdrop-blur-md p-4 md:p-6 rounded-2xl md:rounded-3xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center reveal hover:shadow-2xl hover:scale-105 hover:border-blue-300/60 dark:hover:border-blue-400 transition-all duration-300" :style="{ transitionDelay: `${i * 100}ms` }">
            <component :is="stat.icon" class="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-4 text-blue-500 dark:text-blue-400" />
            <div class="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ stat.value }}</div>
            <div class="text-xs md:text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wider font-semibold leading-tight">{{ stat.label }}</div>
          </div>
        </div>
      </div>
      
      <!-- Wellenförmige Trennlinie -->
      <WaveDivider />
    </section>

    <!-- Features Section -->
    <section class="py-20 pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm relative">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Highlights & Besonderheiten
          </h2>
          <div class="mt-4 h-2 w-24 bg-blue-500 mx-auto rounded-full"></div>
          <p class="mt-6 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Moderne Lösungen für effiziente Verkaufsabwicklung
          </p>
        </div>

        <div class="space-y-16 md:space-y-24">
          <div
            v-for="(feature, index) in sortedFeatures"
            :key="feature.id"
            class="feature-item reveal"
            :style="{ transitionDelay: `${index * 100}ms` }"
          >
            <!-- Modern Card Layout: Mobile = Single Container, Desktop = Grid -->
            <div 
              class="relative group flex flex-col md:grid md:grid-cols-2 md:gap-8"
            >
              <!-- Mobile: Gemeinsamer Container für Text und Media -->
              <div class="md:hidden relative bg-gradient-to-br from-white via-white to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover:border-blue-300/60 dark:group-hover:border-blue-400 transition-all duration-500 overflow-hidden">
                <!-- Decorative Background Element -->
                <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                <!-- Text Content (oben auf mobil) -->
                <div class="relative p-6 pb-4">
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="p-2.5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 dark:from-blue-500/20 dark:to-blue-600/20 border border-blue-500/20 dark:border-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-6 h-6 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div class="px-3 py-1 rounded-full bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-800/30 text-blue-500 dark:text-blue-400 text-xs font-bold border border-blue-500/20 dark:border-blue-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    {{ feature.description }}
                  </p>

                    <!-- Tags -->
                    <div v-if="feature.tags" class="relative flex flex-wrap gap-2">
                      <span 
                        v-for="tag in feature.tags" 
                        :key="tag" 
                        class="text-xs font-semibold px-3 py-1 max-w-[45%] bg-blue-50/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-blue-200/50 dark:border-gray-600/50 group-hover:border-blue-400/50 dark:group-hover:border-blue-400 transition-colors duration-300"
                      >
                        {{ tag }}
                      </span>
                    </div>
                </div>

                <!-- Media Container (unten auf mobil) -->
                <div v-if="feature.type !== 'text-only'" class="relative w-full group/media px-4 pb-4">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-blue-600/20 to-blue-500/20 rounded-[2rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full min-h-[300px] bg-gradient-to-br from-white via-blue-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-2xl overflow-hidden shadow-xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover/media:border-blue-300/60 dark:group-hover/media:border-blue-400 transition-all duration-500 flex items-center justify-center p-3">
                    <!-- Single Image -->
                    <div v-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[400px]' : 'h-full min-h-[300px]']" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-lg group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-4 h-4 text-blue-500 dark:text-blue-400" />
                          <span class="text-xs font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Hover Glow Effect -->
                <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/0 via-blue-500/0 to-blue-500/0 group-hover:from-blue-500/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
              </div>

              <!-- Desktop: Feature Content Card -->
              <div 
                class="hidden md:block relative"
                :class="index % 2 === 1 && feature.type !== 'text-only' ? 'md:order-2' : feature.type === 'text-only' ? 'md:col-span-2' : ''"
              >
                <div class="relative h-full bg-gradient-to-br from-white via-white to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl p-10 shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover:border-blue-300/60 dark:group-hover:border-blue-400 transition-all duration-500 overflow-hidden" :class="feature.type === 'text-only' ? 'min-h-[400px]' : ''">
                  <!-- Decorative Background Element -->
                  <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-6">
                    <div class="flex items-center gap-4">
                      <div class="p-3 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 dark:from-blue-500/20 dark:to-blue-600/20 border border-blue-500/20 dark:border-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-7 h-7 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div class="px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-800/30 text-blue-500 dark:text-blue-400 text-xs font-bold border border-blue-500/20 dark:border-blue-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                    {{ feature.description }}
                  </p>

                  <!-- Tags -->
                  <div v-if="feature.tags" class="relative flex flex-wrap gap-2.5">
                    <span 
                      v-for="tag in feature.tags" 
                      :key="tag" 
                      class="text-xs font-semibold px-4 py-1.5 bg-blue-50/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-blue-200/50 dark:border-gray-600/50 group-hover:border-blue-400/50 dark:group-hover:border-blue-400 transition-colors duration-300"
                    >
                      {{ tag }}
                    </span>
                  </div>

                  <!-- Hover Glow Effect -->
                  <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/0 via-blue-500/0 to-blue-500/0 group-hover:from-blue-500/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
                </div>
              </div>

              <!-- Desktop: Feature Media Container -->
              <div 
                v-if="feature.type !== 'text-only'"
                class="hidden md:block relative"
                :class="index % 2 === 1 ? 'md:order-1' : ''"
              >
                <div class="relative w-full h-full min-h-[500px] flex items-center justify-center group/media">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-6 bg-gradient-to-r from-blue-500/20 via-blue-600/20 to-blue-500/20 rounded-[2.5rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full h-full bg-gradient-to-br from-white via-blue-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-3xl overflow-hidden shadow-2xl border-2 border-blue-100/60 dark:border-gray-700/50 group-hover/media:border-blue-300/60 dark:group-hover/media:border-blue-400 transition-all duration-500 flex items-center justify-center p-6">
                    <!-- Single Image -->
                    <div v-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-2xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[500px]' : 'h-full']" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-xl group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-5 h-5 text-blue-500 dark:text-blue-400" />
                          <span class="text-sm font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Wellenförmige Trennlinie -->
      <WaveDivider :show="shouldShowDivider" />
    </section>

    <!-- Project Info Grid -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-blue-300/60 dark:hover:border-blue-400 transition-all duration-300 reveal">
            <h4 class="text-blue-500 dark:text-blue-400 font-bold mb-4 uppercase tracking-wider text-sm">Plattform</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">React & Firebase</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-blue-300/60 dark:hover:border-blue-400 transition-all duration-300 reveal delay-100">
            <h4 class="text-blue-500 dark:text-blue-400 font-bold mb-4 uppercase tracking-wider text-sm">Fokus</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Offline & Performance</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-blue-100/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl hover:border-blue-300/60 dark:hover:border-blue-400 transition-all duration-300 reveal delay-200">
            <h4 class="text-blue-500 dark:text-blue-400 font-bold mb-4 uppercase tracking-wider text-sm">Zielgruppe</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Mobile Verkaufsstandorte</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Back to Projekte -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50/40 dark:bg-black/10 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto text-center reveal">
        <router-link
          to="/portfolio"
          class="inline-flex items-center space-x-3 px-10 py-4 bg-white dark:bg-gray-800 text-blue-500 dark:text-blue-400 font-bold rounded-2xl shadow-xl hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300 hover:scale-105 border-2 border-blue-500/30 dark:border-blue-400/30 hover:border-blue-400/50 dark:hover:border-blue-400/50"
        >
          <ArrowLeftIcon class="w-5 h-5" />
          <span>Zurück zur Übersicht</span>
        </router-link>
      </div>
    </section>

    <StandardFooter accent-color="#3b82f6" />

    <!-- Lightbox Modal -->
    <Transition name="fade">
      <div v-if="lightboxUrl" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" @click="closeLightbox">
        <button class="absolute top-6 right-6 text-white hover:text-blue-500 transition-colors p-2" @click="closeLightbox">
          <XMarkIcon class="w-8 h-8" />
        </button>
        <img :src="lightboxUrl" alt="Lightbox Image" class="max-w-full max-h-full object-contain rounded-lg shadow-2xl" @click.stop />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onUnmounted, watch } from 'vue'
import Navigation from '../../components/Navigation.vue'
import StandardFooter from '../../components/StandardFooter.vue'
import WaveDivider from '../../components/WaveDivider.vue'
import { useTheme } from '../../composables/useTheme'
import { getPortfolioProjectByPageUrl } from '../../firebase/portfolio'
import { logger } from '../../utils/logger'
import { 
  ArrowLeftIcon, 
  ShoppingCartIcon,
  DevicePhoneMobileIcon,
  Cog6ToothIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  WifiIcon,
  CurrencyDollarIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import type { Component } from 'vue'

const { isDark } = useTheme()

// Logo aus Firebase laden
const logoUrl = ref<string>('')

// Logo aktualisieren, wenn Theme wechselt
const updateLogo = async () => {
  try {
    const project = await getPortfolioProjectByPageUrl('/projects/verkaufszaehler')
    if (project) {
      if (isDark.value && project.logoDark) {
        logoUrl.value = project.logoDark
      } else if (!isDark.value && project.logoLight) {
        logoUrl.value = project.logoLight
      } else {
        logoUrl.value = ''
      }
    } else {
      logoUrl.value = ''
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Projekt-Daten', error)
    logoUrl.value = ''
  }
}

// Alias für initiales Laden
const loadProjectData = updateLogo

// Watch für Theme-Änderungen
const stopWatch = watch(isDark, () => {
  updateLogo()
}, { immediate: false })

// Feature Type Definitions
interface BaseFeature {
  id: string
  title: string
  description: string
  tags?: string[]
  icon: Component
  order: number
}

interface ImageFeature extends BaseFeature {
  type: 'image'
  content: string
  mobileImageStyle?: boolean
}

interface TextOnlyFeature extends BaseFeature {
  type: 'text-only'
  content: string
}

type ProjectFeature = ImageFeature | TextOnlyFeature

const showAllTechs = ref(false)
const isMobile = ref(false)

const checkIfMobile = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

const displayedTechnologies = computed(() => {
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    return project.value.technologies
  }
  // Auf mobil: Wenn "Mehr anzeigen" geklickt wurde, alle anzeigen, sonst nur erste 8
  if (showAllTechs.value) {
    return project.value.technologies
  }
  return project.value.technologies.slice(0, 8)
})

const stats = [
  { icon: DevicePhoneMobileIcon, value: 'Tablet', label: 'Optimiert' },
  { icon: WifiIcon, value: 'Offline', label: 'Fähig' },
  { icon: RocketLaunchIcon, value: 'Schnell', label: 'Performance' },
  { icon: ChartBarIcon, value: 'Detailliert', label: 'Statistiken' }
]

interface Project {
  technologies: string[]
  features: ProjectFeature[]
}

const project = ref<Project>({
  technologies: ['React 18.3', 'TypeScript', 'Vite', 'Tailwind CSS 4.0', 'Firebase Firestore', 'Firebase Auth', 'Offline Support', 'Responsive Design'],
  features: [
    {
      id: 'feature-dashboard',
      title: 'Intuitives Verkaufs-Dashboard',
      description: 'Das übersichtliche Grid-Layout mit farbkodierten Kacheln ist speziell für die einfache Bedienung auf Tablets optimiert. Artikel werden als große, gut erkennbare Kacheln dargestellt, die sich schnell per Touch auswählen lassen. Ein integriertes Warenkorb-System berechnet in Echtzeit Gesamtpreis, Pfand und Rückgeld. Zusätzliche Funktionen wie Kategorien-Filter, Suchfunktion und Sortierung ermöglichen eine schnelle Artikelauswahl.',
      type: 'image',
      content: '/projects/verkaufszaehler/dashboard.webp',
      tags: ['Touch-Optimiert', 'Echtzeit-Berechnung', 'Intuitive Bedienung'],
      icon: ShoppingCartIcon,
      order: 1
    },
    {
      id: 'feature-admin',
      title: 'Umfassender Admin-Bereich',
      description: 'Der Admin-Bereich bietet vollständige Kontrolle über Katalog, Artikel und Benutzer. Kategorien können mit individuellen Farben angelegt und per Drag & Drop sortiert werden. Artikel werden mit Preisen, Kategorienzuordnung und Pfand-Verknüpfung verwaltet. Ein flexibles Benutzerverwaltungssystem ermöglicht es, Kategorien pro Nutzer sichtbar oder unsichtbar zu schalten, was personalisierte Dashboards ermöglicht. Zusätzlich können Bestellungen verwaltet, bearbeitet und Zahlungsarten nachträglich geändert werden.',
      type: 'text-only',
      content: '',
      tags: ['Katalogverwaltung', 'Drag & Drop', 'Benutzerverwaltung'],
      icon: Cog6ToothIcon,
      order: 2
    },
    {
      id: 'feature-pfand',
      title: 'Intelligentes Pfandsystem',
      description: 'Das integrierte Pfandsystem ermöglicht eine automatische Pfandberechnung basierend auf der Artikelmenge. Pfand-Artikel wie Becher oder Teller können angelegt und automatisch Artikeln zugeordnet werden. Ein dediziertes Modal zur Erfassung zurückgebrachter Pfand-Artikel sorgt für eine saubere Trennung von Verkauf und Rückgabe. Separate Auswertungen zeigen eingenommenes und zurückgegebenes Pfand mit detaillierter Bilanz.',
      type: 'text-only',
      content: '',
      tags: ['Automatische Berechnung', 'Pfandrückgabe', 'Statistiken'],
      icon: CurrencyDollarIcon,
      order: 3
    },
    {
      id: 'feature-offline',
      title: 'Offline-Funktionalität & Performance',
      description: 'Dank Firebase Persistent Local Cache funktioniert die Anwendung auch ohne Internetverbindung. Ein visueller Online-Status-Indikator zeigt die aktuelle Verbindungsqualität an. Robuste Fehlerbehandlung durch Error Boundaries sorgt für einen unterbrechungsfreien Betrieb. Die Echtzeit-Synchronisation über Firebase Firestore ermöglicht Multi-Tab-Support und Live-Updates zwischen mehreren geöffneten Tabs.',
      type: 'text-only',
      content: '',
      tags: ['Offline-First', 'Echtzeit-Sync', 'Error Handling'],
      icon: WifiIcon,
      order: 4
    },
    {
      id: 'feature-statistics',
      title: 'Detaillierte Statistiken & Auswertungen',
      description: 'Umfassende Auswertungen ermöglichen detaillierte Einblicke in den Verkaufsbetrieb. Umsatzstatistiken können nach Tag, 7 Tage oder Jahr gefiltert werden. Kategorien-Analysen zeigen die Umsatzverteilung mit Prozentangaben, während Top-Artikel-Listen Bestseller mit Mengen und Umsätzen präsentieren. Separate Auswertungen für Bar- und Kartenzahlungen mit Durchschnittswerten sowie eine Pfand-Bilanz runden das Angebot ab. Professionelle PDF-Reports mit Logo und strukturierten Tabellen können exportiert werden.',
      type: 'text-only',
      content: '',
      tags: ['Umsatzstatistiken', 'PDF-Export', 'Detaillierte Analysen'],
      icon: ChartBarIcon,
      order: 5
    }
  ]
})

// Scroll Animation Logic
const initScrollReveal = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
}

const sortedFeatures = computed(() => {
  return [...project.value.features].sort((a, b) => a.order - b.order)
})

// Lightbox Logic
const lightboxUrl = ref<string | null>(null)
const openLightbox = (url: string) => {
  lightboxUrl.value = url
  document.body.style.overflow = 'hidden'
}
const closeLightbox = () => {
  lightboxUrl.value = null
  document.body.style.overflow = ''
}

const shouldShowDivider = computed(() => {
  return false
})

const handleResize = () => {
  isMobile.value = checkIfMobile()
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    showAllTechs.value = true
  }
}

// Handler für pageshow Event
const handlePageShow = (event: PageTransitionEvent) => {
  if (event.persisted) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
}

onBeforeMount(() => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
})

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  
  // Lade Projekt-Daten und Logo
  await loadProjectData()
  
  // Prüfe ob mobil
  isMobile.value = checkIfMobile()
  if (!isMobile.value) {
    showAllTechs.value = true
  }
  
  // Listener für Resize hinzufügen
  window.addEventListener('resize', handleResize)
  window.addEventListener('pageshow', handlePageShow)
  
  initScrollReveal()
  // Füge Klasse zu html hinzu für Scrollbar-Styling
  document.documentElement.classList.add('verkaufszaehler-page')
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('pageshow', handlePageShow)
  // Stoppe Watch für Theme-Änderungen
  stopWatch()
  // Entferne Klasse von html
  document.documentElement.classList.remove('verkaufszaehler-page')
})
</script>

<style scoped>
@keyframes blob-organic {
  0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
  33% { transform: translate(40px, -60px) scale(1.15) rotate(5deg); }
  66% { transform: translate(-30px, 30px) scale(0.95) rotate(-5deg); }
  100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
}

.animate-blob-organic {
  animation: blob-organic 10s infinite ease-in-out;
}

.animation-delay-1500 {
  animation-delay: 1.5s;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Scroll Reveal */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.delay-100 { transition-delay: 0.1s; }
.delay-200 { transition-delay: 0.2s; }
.delay-300 { transition-delay: 0.3s; }
.delay-400 { transition-delay: 0.4s; }
.delay-500 { transition-delay: 0.5s; }

/* Fade Animation (Lightbox) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Mauszeiger und Scrollwheel in Featurefarbe für Verkaufszähler Seite */
:deep(.outer-ring) {
  border-color: rgba(59, 130, 246, 0.3) !important;
}

:deep(.outer-ring.hover-active) {
  border-color: rgba(59, 130, 246, 0.4) !important;
}

:deep(.custom-cursor) {
  background: rgba(59, 130, 246, 0.6) !important;
  box-shadow: 
    0 0 10px rgba(59, 130, 246, 0.5),
    0 0 20px rgba(59, 130, 246, 0.3),
    inset 0 0 10px rgba(59, 130, 246, 0.2) !important;
}

:deep(.custom-cursor.hover-active) {
  background: rgba(59, 130, 246, 0.7) !important;
  box-shadow: 
    0 0 15px rgba(59, 130, 246, 0.6),
    0 0 30px rgba(59, 130, 246, 0.4),
    0 0 45px rgba(59, 130, 246, 0.2),
    inset 0 0 15px rgba(59, 130, 246, 0.3) !important;
}

:deep(.trail-dot) {
  background: rgba(59, 130, 246, 0.5) !important;
}
</style>

<style>
/* Scrollwheel in Featurefarbe für Verkaufszähler Seite */
html.verkaufszaehler-page,
html.verkaufszaehler-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(59, 130, 246, 0.6) rgba(0, 0, 0, 0.05) !important;
}

html.verkaufszaehler-page ::-webkit-scrollbar,
html.verkaufszaehler-page body ::-webkit-scrollbar {
  width: 12px !important;
  height: 12px !important;
}

html.verkaufszaehler-page ::-webkit-scrollbar-track,
html.verkaufszaehler-page body ::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05) !important;
  border-left: none !important;
  border-radius: 0 !important;
}

html.verkaufszaehler-page ::-webkit-scrollbar-thumb,
html.verkaufszaehler-page body ::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.6) !important;
  background-image: none !important;
  border-radius: 6px !important;
  border: 2px solid transparent !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.verkaufszaehler-page ::-webkit-scrollbar-thumb:hover,
html.verkaufszaehler-page body ::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.8) !important;
  background-image: none !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.verkaufszaehler-page ::-webkit-scrollbar-thumb:active,
html.verkaufszaehler-page body ::-webkit-scrollbar-thumb:active {
  background: rgba(59, 130, 246, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Dark Mode Scrollwheel für Verkaufszähler Seite */
html.verkaufszaehler-page.dark,
html.verkaufszaehler-page.dark * {
  scrollbar-color: rgba(59, 130, 246, 0.7) rgba(255, 255, 255, 0.05) !important;
}

html.verkaufszaehler-page.dark ::-webkit-scrollbar-track,
html.verkaufszaehler-page.dark body ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05) !important;
  border-left: none !important;
}

html.verkaufszaehler-page.dark ::-webkit-scrollbar-thumb,
html.verkaufszaehler-page.dark body ::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.7) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
}

html.verkaufszaehler-page.dark ::-webkit-scrollbar-thumb:hover,
html.verkaufszaehler-page.dark body ::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.verkaufszaehler-page.dark ::-webkit-scrollbar-thumb:active,
html.verkaufszaehler-page.dark body ::-webkit-scrollbar-thumb:active {
  background: rgba(96, 165, 250, 1) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* IT Text Unterstreichung in Verkaufszähler Farbe */
html.verkaufszaehler-page .it-text::after {
  background-color: rgb(59, 130, 246) !important; /* blue-500 */
}

html.verkaufszaehler-page.dark .it-text::after {
  background-color: rgb(96, 165, 250) !important; /* blue-400 */
}
</style>

